import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip, Button,
  LinearProgress, Avatar, Grow, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Tooltip, Accordion, AccordionSummary, AccordionDetails, Tabs, Tab,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  CircularProgress, Divider, TextField, List, ListItem, ListItemText
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../../config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import ChecklistIcon from '@mui/icons-material/Checklist';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha';
import BarChartIcon from '@mui/icons-material/BarChart';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import CloseIcon from '@mui/icons-material/Close';
import InsightsIcon from '@mui/icons-material/Insights';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, Tooltip as ChartTooltip, Legend,
  BarElement, BarController, CategoryScale, LinearScale, LineElement, PointElement, LineController
} from 'chart.js';

ChartJS.register(ChartTooltip, Legend, BarElement, BarController, CategoryScale, LinearScale, LineElement, PointElement, LineController);

function formatCurrency(amount) {
  if (amount == null) return '$0';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  const d = new Date(dateStr);
  return Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const PALETTE = ['#6366f1', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

function QuoteDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [allQuotes, setAllQuotes] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [tabValue, setTabValue] = useState(0);

  // ── Forecast state ──
  const [forecastOpen, setForecastOpen] = useState(false);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [forecastError, setForecastError] = useState(null);
  const [forecastGroupLabel, setForecastGroupLabel] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const passedQuotes = location.state?.quotes || [];
  const passedOpenIds = location.state?.openQuoteIds || [];

  useEffect(() => {
    setAllQuotes(passedQuotes);
    setLoading(false);
  }, []);

  const openQuotes = React.useMemo(() =>
    allQuotes.filter(q => q.statecode !== 2 && q.statecode !== 3)
      .sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0)),
    [allQuotes]
  );

  const groupedByCountryDiv = React.useMemo(() => {
    const groups = {};
    openQuotes.forEach(q => {
      const c = q.country || 'Unknown';
      const d = q.division || 'Unknown';
      const key = `${c}|||${d}`;
      if (!groups[key]) groups[key] = { country: c, division: d, quotes: [], totalValue: 0, count: 0 };
      groups[key].quotes.push(q);
      groups[key].totalValue += (q.total_amount || 0);
      groups[key].count++;
    });
    const grouped = Object.values(groups).sort((a, b) => b.totalValue - a.totalValue);
    if (Object.keys(expandedGroups).length === 0 && grouped.length > 0) {
      const defaults = {};
      grouped.forEach((_, i) => { defaults[i] = true; });
      setTimeout(() => setExpandedGroups(defaults), 50);
    }
    return grouped;
  }, [openQuotes]);

  const metrics = React.useMemo(() => {
    let totalValue = 0, maxValue = 0;
    openQuotes.forEach(q => {
      const amt = q.total_amount || 0;
      totalValue += amt;
      if (amt > maxValue) maxValue = amt;
    });
    const avgValue = openQuotes.length > 0 ? totalValue / openQuotes.length : 0;
    const top10Count = Math.ceil(openQuotes.length * 0.1);
    const top10Value = openQuotes.slice(0, top10Count).reduce((s, q) => s + (q.total_amount || 0), 0);
    const top10Pct = totalValue > 0 ? Math.round((top10Value / totalValue) * 100) : 0;
    const staleCount = openQuotes.filter(q => daysSince(q.createdon) > 60).length;
    return { totalValue, maxValue, avgValue, top10Value, top10Pct, staleCount };
  }, [openQuotes]);

  const monthlyChartData = React.useMemo(() => {
    const months = {};
    allQuotes.forEach(q => {
      if (!q.createdon) return;
      const d = new Date(q.createdon);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
      if (!months[key]) months[key] = { key, label, count: 0, totalValue: 0 };
      months[key].count++;
      months[key].totalValue += (q.total_amount || 0);
    });
    const sorted = Object.values(months).sort((a, b) => a.key.localeCompare(b.key));
    return {
      labels: sorted.map(m => m.label),
      datasets: [
        { type: 'bar', label: 'Total Amount', data: sorted.map(m => m.totalValue), backgroundColor: 'rgba(59,130,246,0.6)', borderColor: '#3b82f6', borderWidth: 1, borderRadius: 6, yAxisID: 'y', order: 2 },
        { type: 'line', label: 'Quote Count', data: sorted.map(m => m.count), borderColor: '#10b981', backgroundColor: '#10b981', borderWidth: 2.5, pointRadius: 5, pointBackgroundColor: '#10b981', pointBorderColor: '#fff', pointBorderWidth: 2, tension: 0.3, fill: false, yAxisID: 'y1', order: 1 }
      ]
    };
  }, [allQuotes]);

  const allQuotesByAmount = React.useMemo(() =>
    [...allQuotes].sort((a, b) => (b.total_amount || 0) - (a.total_amount || 0)),
    [allQuotes]
  );

  const countryStats = React.useMemo(() => {
    const cnts = {};
    allQuotes.forEach(q => {
      const c = q.country || 'Unknown';
      if (!cnts[c]) cnts[c] = { count: 0, totalValue: 0 };
      cnts[c].count++;
      cnts[c].totalValue += (q.total_amount || 0);
    });
    const totalPipeline = Object.values(cnts).reduce((s, v) => s + v.totalValue, 0);
    const entries = Object.entries(cnts)
      .map(([k, v]) => ({ name: k, count: v.count, totalValue: v.totalValue, pct: totalPipeline > 0 ? Math.round((v.totalValue / totalPipeline) * 100) : 0 }))
      .sort((a, b) => b.totalValue - a.totalValue);
    return { data: entries, totalPipeline };
  }, [allQuotes]);

  const divisionStats = React.useMemo(() => {
    const divs = {};
    allQuotes.forEach(q => {
      const d = q.division || 'Unknown';
      if (!divs[d]) divs[d] = { count: 0, totalValue: 0 };
      divs[d].count++;
      divs[d].totalValue += (q.total_amount || 0);
    });
    const totalPipeline = Object.values(divs).reduce((s, v) => s + v.totalValue, 0);
    const entries = Object.entries(divs)
      .map(([k, v]) => ({ name: k, count: v.count, totalValue: v.totalValue, pct: totalPipeline > 0 ? Math.round((v.totalValue / totalPipeline) * 100) : 0 }))
      .sort((a, b) => b.totalValue - a.totalValue);
    return { data: entries, totalPipeline };
  }, [allQuotes]);

  const handleToggleGroup = (idx) => setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));

  // ── Forecast functions ──
  const handleOpenForecast = (group) => {
    setForecastGroupLabel(`${group.country} / ${group.division}`);
    setForecastData(null);
    setForecastError(null);
    setForecastOpen(true);
    // Auto-run forecast immediately
    handleRunForecast(group);
  };

  const handleCloseForecast = () => {
    setForecastOpen(false);
    setForecastData(null);
    setForecastError(null);
  };

  const handleRunForecast = async (group) => {
    setForecastLoading(true);
    setForecastError(null);
    setForecastData(null);

    const firstQuote = group.quotes && group.quotes.length > 0 ? group.quotes[0] : {};
    const quotationIds = (group.quotes || []).map(q => q.quoteid).filter(id => id);

    // Compute date range from the group's quotes' createdon
    const quoteDates = (group.quotes || [])
      .map(q => q.createdon)
      .filter(d => d)
      .sort();
    const fromdate = quoteDates.length > 0 ? quoteDates[0].split('T')[0] : '';
    const todate = quoteDates.length > 0 ? quoteDates[quoteDates.length - 1].split('T')[0] : '';

    const payload = {
      quotation_ids: quotationIds,
      country_id: firstQuote.country_id || group.country,
      division_id: firstQuote.division_id || group.division,
      fromdate: fromdate,
      todate: todate
    };

    try {
      const res = await axios.post(`${API_URL}/quotes/forecast`, payload, { headers });
      if (res.data && res.data.status === 'success') {
        setForecastData(res.data);
      } else {
        setForecastError(res.data?.message || 'No forecast data returned');
      }
    } catch (err) {
      setForecastError(err.response?.data?.message || err.message || 'Failed to fetch forecast');
    } finally {
      setForecastLoading(false);
    }
  };

  const getProbabilityColor = (prob) => {
    if (prob >= 0.6) return '#10b981';
    if (prob >= 0.3) return '#f59e0b';
    return '#ef4444';
  };

  const getProbabilityIcon = (prob) => {
    if (prob >= 0.6) return <TrendingUpIcon sx={{ fontSize: 18 }} />;
    if (prob >= 0.3) return <TrendingFlatIcon sx={{ fontSize: 18 }} />;
    return <TrendingDownIcon sx={{ fontSize: 18 }} />;
  };

  const getHorizonColor = (horizon) => {
    switch (horizon) {
      case '3M': return '#10b981';
      case '6M': return '#3b82f6';
      case '1Y': return '#8b5cf6';
      case 'STALE': return '#ef4444';
      default: return '#64748b';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 4, bgcolor: '#f4f6f8' }}>
        <Box sx={{ width: 80, height: 80, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', boxShadow: '0 10px 30px rgba(30,60,114,0.2)' }}>
          <DashboardIcon sx={{ fontSize: 42, color: '#90caf9' }} />
        </Box>
        <Typography variant="h5" fontWeight="700" color="#1e293b">Loading Quote Dashboard...</Typography>
        <Box sx={{ width: 400, maxWidth: '80%' }}>
          <LinearProgress sx={{ borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#3b82f6' } }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: '#f4f6f8', minHeight: '100vh', pb: 8 }}>
      <Grow in={true} timeout={500}>
        <Box sx={{ mb: 4, mt: 2, mx: { xs: 1, md: 3 }, p: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: 'white', boxShadow: '0 10px 30px rgba(30,60,114,0.2)', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 3 }}>
          <Box sx={{ position: 'relative', zIndex: 2 }}>
            <Typography variant="h4" fontWeight="800" sx={{ mb: 1, letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 2, color: 'white' }}><DashboardIcon sx={{ fontSize: 40, color: '#90caf9' }} /> Pipeline & Quote Dashboard</Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>{allQuotes.length} total quotes • {openQuotes.length} open • {formatCurrency(metrics.totalValue)} pipeline</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, position: 'relative', zIndex: 2 }}>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 2, fontWeight: 'bold', px: 3, py: 1, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', boxShadow: 'none', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}>Back to Quotes</Button>
          </Box>
        </Box>
      </Grow>

      <Box sx={{ px: { xs: 2, md: 3 }, maxWidth: 1400, mx: 'auto' }}>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><ChecklistIcon sx={{ fontSize: 28, color: '#6366f1', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#1e293b">{allQuotes.length}</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Total Quotes</Typography></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><TrendingUpIcon sx={{ fontSize: 28, color: '#3b82f6', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#3b82f6">{openQuotes.length}</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Open</Typography></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><AttachMoneyIcon sx={{ fontSize: 28, color: '#10b981', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#10b981">{formatCurrency(metrics.totalValue)}</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Pipeline</Typography></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><BarChartIcon sx={{ fontSize: 28, color: '#8b5cf6', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#8b5cf6">{formatCurrency(metrics.avgValue)}</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Avg Value</Typography></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><WarningAmberIcon sx={{ fontSize: 28, color: '#f59e0b', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#ef4444">{metrics.staleCount}</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Stale {'>'}60d</Typography></Card></Grid>
          <Grid item xs={6} sm={4} md={2}><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}><PriorityHighIcon sx={{ fontSize: 28, color: '#10b981', mb: 0.5 }} /><Typography variant="h5" fontWeight="800" color="#10b981">{metrics.top10Pct}%</Typography><Typography variant="caption" fontWeight="600" color="#64748b">Top 10%</Typography></Card></Grid>
        </Grid>

        <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: 'white', overflow: 'hidden' }}>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc', px: 2, '& .MuiTab-root': { fontWeight: 700, py: 2, color: '#64748b', '&.Mui-selected': { color: '#1e3c72' } } }}>
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Open by Country/Div" />
            <Tab icon={<CalendarMonthIcon />} iconPosition="start" label="Month Overview" />
            <Tab icon={<SortByAlphaIcon />} iconPosition="start" label="All by Amount" />
            <Tab icon={<PublicIcon />} iconPosition="start" label="Pipeline by Country" />
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Pipeline by Division" />
          </Tabs>

          {/* TAB 0 — Open by Country/Div */}
          {tabValue === 0 && <Box sx={{ p: 3 }}>{groupedByCountryDiv.map((grp, idx) => {
            const hue = (idx * 47 + 210) % 360;
            const isExpanded = expandedGroups[idx] !== false;
            return <Card key={idx} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', mb: 2, overflow: 'hidden' }}>
              <Accordion expanded={isExpanded} onChange={() => handleToggleGroup(idx)} sx={{ boxShadow: 'none', border: 'none', bgcolor: 'transparent', '&:before': { display: 'none' }, '&.Mui-expanded': { m: 0 } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: '4px 20px', minHeight: '56px !important', '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5, my: '8px !important' }, bgcolor: isExpanded ? '#f0f5ff' : 'white' }}>
                  <Avatar sx={{ bgcolor: `hsl(${hue},55%,55%)`, color: 'white', width: 40, height: 40, fontWeight: 800, fontSize: '1rem', borderRadius: 2 }}>{grp.country?.charAt(0).toUpperCase()}</Avatar>
                  <Box sx={{ flex: 1 }}><Typography variant="subtitle1" fontWeight="800" color="#1e293b"><Box component="span" sx={{ color: `hsl(${hue},55%,50%)` }}>{grp.country}</Box><Box component="span" sx={{ color: '#cbd5e1', mx: 0.5 }}>/</Box>{grp.division}</Typography><Typography variant="caption" color="#64748b">{grp.count} quotes • {formatCurrency(grp.totalValue)}</Typography></Box>
                  <Chip label={`${grp.count}`} size="small" sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontWeight: 700, borderRadius: 1.5, height: 24 }} />
                  <Chip label={formatCurrency(grp.totalValue)} size="small" sx={{ bgcolor: '#f0fdf4', color: '#166534', fontWeight: 700, borderRadius: 1.5, height: 24, fontSize: '0.7rem' }} />
                  <Tooltip title="Run AI Forecast for this group" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleOpenForecast(grp); }}
                      sx={{
                        bgcolor: 'rgba(99,102,241,0.1)', color: '#6366f1', ml: 1,
                        '&:hover': { bgcolor: 'rgba(99,102,241,0.2)' }
                      }}
                    >
                      <QueryStatsIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, maxHeight: 500, overflow: 'auto' }}><TableContainer><Table size="small"><TableHead sx={{ bgcolor: '#f8fafc' }}><TableRow>{['#','QUOTE / REF','CUSTOMER','AMOUNT','STATUS','AGE','CREATED'].map(h => <TableCell key={h} sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.72rem' }}>{h}</TableCell>)}</TableRow></TableHead>
                  <TableBody>{grp.quotes.map((q, qi) => { const a = daysSince(q.createdon), s = a > 60, o = a > 30 && a <= 60; return <TableRow key={q.quoteid} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' }, cursor: 'pointer', bgcolor: s ? '#fef2f2' : o ? '#fffbeb' : 'transparent' }} onClick={() => navigate(`/quotes/${q.quoteid}`)}>
                    <TableCell sx={{ fontWeight: 700, color: '#94a3b8', fontSize: '0.8rem' }}>{qi + 1}</TableCell>
                    <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 28, height: 28, fontWeight: 800, fontSize: '0.6rem', borderRadius: 1.5 }}>{(q.quote_ref || q.quote_number || 'Q').substring(0, 2)}</Avatar><Box><Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.8rem' }}>{q.name}</Typography><Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, fontSize: '0.65rem' }}>{q.quote_ref || q.quote_number}</Typography></Box></Box></TableCell>
                    <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.8rem' }}>{q.customer_name || '—'}</TableCell>
                    <TableCell sx={{ color: '#10b981', fontWeight: 700, fontSize: '0.85rem' }}>{formatCurrency(q.total_amount)}</TableCell>
                    <TableCell><Chip label={q.status || 'Active'} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.65rem', bgcolor: '#eff6ff', color: '#1e40af' }} /></TableCell>
                    <TableCell>{s ? <Tooltip title="Over 60 days" arrow><Chip icon={<WarningAmberIcon sx={{ fontSize: 12 }} />} label={`${a}d`} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.65rem', bgcolor: '#fef2f2', color: '#b91c1c' }} /></Tooltip> : o ? <Tooltip title="Over 30 days" arrow><Chip icon={<ScheduleIcon sx={{ fontSize: 12 }} />} label={`${a}d`} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.65rem', bgcolor: '#fffbeb', color: '#b45309' }} /></Tooltip> : <Chip label={`${a}d`} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 22, fontSize: '0.65rem', bgcolor: '#f1f5f9', color: '#475569' }} />}</TableCell>
                    <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.8rem' }}>{formatDate(q.createdon)}</TableCell>
                  </TableRow>; })}</TableBody></Table></TableContainer></AccordionDetails>
              </Accordion>
            </Card>;
          })}</Box>}

          {/* TAB 1 — Month Overview Chart */}
          {tabValue === 1 && <Box sx={{ p: 3 }}><Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5}><CalendarMonthIcon sx={{ color: '#3b82f6', mr: 1 }} /> Monthly Overview — Amount + Quote Count</Typography><Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', p: 3 }}><Box sx={{ width: '100%', height: 420 }}><Bar data={monthlyChartData} options={{ responsive: true, maintainAspectRatio: false, animation: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'top', labels: { padding: 16, usePointStyle: true, font: { size: 12, weight: '600' }, color: '#475569' } }, tooltip: { backgroundColor: 'rgba(30,41,59,0.95)', titleFont: { weight: '700', size: 14 }, bodyFont: { size: 13 }, padding: 12, cornerRadius: 8, callbacks: { label: ctx => ctx.dataset.label === 'Total Amount' ? ` ${ctx.dataset.label}: ${formatCurrency(ctx.raw)}` : ` ${ctx.dataset.label}: ${ctx.raw} quotes` } } }, scales: { x: { grid: { display: false }, ticks: { color: '#64748b', font: { weight: '600', size: 11 } } }, y: { type: 'linear', position: 'left', beginAtZero: true, grid: { color: '#f1f5f9' }, ticks: { color: '#3b82f6', font: { size: 11 }, callback: v => v >= 1000000 ? '$' + (v / 1000000).toFixed(1) + 'M' : v >= 1000 ? '$' + (v / 1000).toFixed(0) + 'K' : '$' + v } }, y1: { type: 'linear', position: 'right', beginAtZero: true, grid: { drawOnChartArea: false }, ticks: { color: '#10b981', font: { size: 11 }, stepSize: 1 } } } }} /></Box></Card></Box>}

          {/* TAB 2 — All by Amount */}
          {tabValue === 2 && <Box sx={{ p: 3 }}><Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5}><SortByAlphaIcon sx={{ color: '#3b82f6', mr: 1 }} /> All Quotes — Sorted by Amount</Typography><TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9' }}><Table size="medium"><TableHead sx={{ bgcolor: '#f8fafc' }}><TableRow>{['#','QUOTE/REF','CUSTOMER','COUNTRY','DIVISION','AMOUNT','STATUS','RESULT','CREATED'].map(h => <TableCell key={h} sx={{ fontWeight: 700, color: '#64748b' }}>{h}</TableCell>)}</TableRow></TableHead><TableBody>{allQuotesByAmount.map((q, i) => <TableRow key={q.quoteid} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9' }, cursor: 'pointer' }} onClick={() => navigate(`/quotes/${q.quoteid}`)}>
            <TableCell sx={{ fontWeight: 700, color: '#94a3b8' }}>{i + 1}</TableCell>
            <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ bgcolor: '#3b82f6', color: 'white', width: 30, height: 30, fontWeight: 800, fontSize: '0.65rem', borderRadius: 1.5 }}>{(q.quote_ref || q.quote_number || 'Q').substring(0, 2)}</Avatar><Box><Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.85rem' }}>{q.name}</Typography><Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600 }}>{q.quote_ref || q.quote_number}</Typography></Box></Box></TableCell>
            <TableCell sx={{ color: '#334155', fontWeight: 600, fontSize: '0.85rem' }}>{q.customer_name || '—'}</TableCell>
            <TableCell sx={{ color: '#334155', fontWeight: 600 }}>{q.country || '—'}</TableCell>
            <TableCell sx={{ color: '#334155', fontWeight: 600 }}>{q.division || '—'}</TableCell>
            <TableCell sx={{ color: '#10b981', fontWeight: 700 }}>{formatCurrency(q.total_amount)}</TableCell>
            <TableCell><Chip label={q.status || 'Draft'} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 24, fontSize: '0.7rem', bgcolor: '#eff6ff', color: '#1e40af' }} /></TableCell>
            <TableCell>{q.closed_status ? <Chip label={q.closed_status} size="small" sx={{ fontWeight: 'bold', borderRadius: 1.5, height: 24, fontSize: '0.7rem', bgcolor: (q.closed_status || '').toLowerCase().includes('won') ? '#dcfce7' : '#fef2f2', color: (q.closed_status || '').toLowerCase().includes('won') ? '#166534' : '#b91c1c' }} /> : <Typography variant="body2" color="#94a3b8">—</Typography>}</TableCell>
            <TableCell sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.85rem' }}>{formatDate(q.createdon)}</TableCell>
          </TableRow>)}</TableBody></Table></TableContainer></Box>}

          {/* TAB 3 — Pipeline by Country */}
          {tabValue === 3 && <Box sx={{ p: 3 }}><Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5}><PublicIcon sx={{ color: '#3b82f6', mr: 1 }} /> Pipeline by Country — {formatCurrency(countryStats.totalPipeline)} total</Typography>
            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', p: 2 }}>
              {countryStats.data.map((c, i) => (
                <Box key={c.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: i < countryStats.data.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 140, flexShrink: 0 }}>
                    <Avatar sx={{ bgcolor: PALETTE[i % PALETTE.length], color: 'white', width: 30, height: 30, fontWeight: 800, fontSize: '0.7rem', borderRadius: 1.5 }}>{c.name?.charAt(0).toUpperCase()}</Avatar>
                    <Box>
                      <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.85rem', lineHeight: 1.2 }}>{c.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>{c.count} quotes</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem', width: 100, flexShrink: 0, textAlign: 'right' }}>{formatCurrency(c.totalValue)}</Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, height: 22, borderRadius: 6, bgcolor: '#f1f5f9', overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ height: '100%', borderRadius: 6, bgcolor: PALETTE[i % PALETTE.length], width: `${Math.max(2, c.pct)}%`, minWidth: 4, transition: 'width 1s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem', textShadow: '0 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>{c.pct}%</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Card>
          </Box>}

          {/* TAB 4 — Pipeline by Division */}
          {tabValue === 4 && <Box sx={{ p: 3 }}><Typography variant="h6" fontWeight="800" color="#1e293b" mb={2.5}><BusinessIcon sx={{ color: '#3b82f6', mr: 1 }} /> Pipeline by Division — {formatCurrency(divisionStats.totalPipeline)} total</Typography>
            <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', p: 2 }}>
              {divisionStats.data.map((d, i) => (
                <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: i < divisionStats.data.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 140, flexShrink: 0 }}>
                    <Avatar sx={{ bgcolor: PALETTE[i % PALETTE.length], color: 'white', width: 30, height: 30, fontWeight: 800, fontSize: '0.7rem', borderRadius: 1.5 }}>{d.name?.charAt(0).toUpperCase()}</Avatar>
                    <Box>
                      <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.85rem', lineHeight: 1.2 }}>{d.name}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.65rem' }}>{d.count} quotes</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: '#10b981', fontSize: '0.85rem', width: 100, flexShrink: 0, textAlign: 'right' }}>{formatCurrency(d.totalValue)}</Typography>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1, height: 22, borderRadius: 6, bgcolor: '#f1f5f9', overflow: 'hidden', position: 'relative' }}>
                      <Box sx={{ height: '100%', borderRadius: 6, bgcolor: PALETTE[i % PALETTE.length], width: `${Math.max(2, d.pct)}%`, minWidth: 4, transition: 'width 1s', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: '0.7rem', textShadow: '0 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>{d.pct}%</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Card>
          </Box>}

        </Card>
      </Box>

      {/* ── Forecast Dialog ── */}
      <Dialog
        open={forecastOpen}
        onClose={handleCloseForecast}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3, py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(144,202,249,0.2)' }}>
              <QueryStatsIcon sx={{ fontSize: 22, color: '#90caf9' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="800" color="white">
                AI Quote Forecast
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {forecastGroupLabel}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseForecast} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#f4f6f8' }}>
          {!forecastLoading && !forecastError && !forecastData && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2.5 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#e8edf5' }}>
                <QueryStatsIcon sx={{ fontSize: 36, color: '#1e3c72' }} />
              </Box>
              <Typography color="#475569" fontWeight="600" textAlign="center">
                Generate AI-powered predictions for quotes in this group
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  const parts = forecastGroupLabel.split(' / ');
                  const match = groupedByCountryDiv.find(g => g.country === parts[0] && g.division === parts[1]);
                  if (match) handleRunForecast(match);
                }}
                disabled={forecastLoading}
                startIcon={<QueryStatsIcon />}
                sx={{
                  mt: 1, borderRadius: 2, fontWeight: 'bold', px: 4, py: 1.2,
                  background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                  boxShadow: '0 4px 15px rgba(30,60,114,0.3)',
                  '&:hover': { boxShadow: '0 6px 20px rgba(30,60,114,0.4)' }
                }}
              >
                Run Forecast
              </Button>
            </Box>
          )}

          {forecastLoading && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 3 }}>
              <CircularProgress size={48} sx={{ color: '#1e3c72' }} />
              <Typography color="#64748b" fontWeight={600}>Running AI forecast analysis...</Typography>
              <LinearProgress sx={{ width: 300, maxWidth: '80%', borderRadius: 2, height: 6, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { bgcolor: '#1e3c72' } }} />
            </Box>
          )}

          {forecastError && !forecastLoading && (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #fecaca', bgcolor: '#fef2f2', p: 4, textAlign: 'center' }}>
              <WarningAmberIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
              <Typography fontWeight="700" color="#b91c1c" mb={2}>{forecastError}</Typography>
              <Button variant="outlined" color="error" onClick={() => {
                const parts = forecastGroupLabel.split(' / ');
                const match = groupedByCountryDiv.find(g => g.country === parts[0] && g.division === parts[1]);
                if (match) handleRunForecast(match);
              }} sx={{ borderRadius: 2 }}>
                Retry
              </Button>
            </Card>
          )}

          {forecastData && !forecastLoading && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={3}>
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}>
                    <Typography variant="caption" fontWeight="600" color="#64748b">Avg Probability (All)</Typography>
                    <Typography variant="h5" fontWeight="800" color={getProbabilityColor(forecastData.summary?.average_probability_all || 0)}>
                      {((forecastData.summary?.average_probability_all || 0) * 100).toFixed(0)}%
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', textAlign: 'center', p: 2, bgcolor: 'white' }}>
                    <Typography variant="caption" fontWeight="600" color="#64748b">Avg Probability (Healthy)</Typography>
                    <Typography variant="h5" fontWeight="800" color="#10b981">
                      {((forecastData.summary?.average_probability_healthy || 0) * 100).toFixed(0)}%
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #f1f5f9', p: 1.5, bgcolor: 'white', height: '100%' }}>
                    <Typography variant="caption" fontWeight="600" color="#64748b" mb={1} display="block">Pipeline by Horizon</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(forecastData.summary?.pipeline_by_horizon ? Object.entries(forecastData.summary.pipeline_by_horizon) : []).map(([key, val]) => (
                        <Chip
                          key={key}
                          label={`${key}: ${formatCurrency(val)}`}
                          size="small"
                          sx={{
                            fontWeight: 700, borderRadius: 1.5,
                            bgcolor: `${getHorizonColor(key)}15`,
                            color: getHorizonColor(key),
                            border: `1px solid ${getHorizonColor(key)}40`
                          }}
                        />
                      ))}
                    </Box>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight="800" color="#1e293b" mb={1.5}>
                {forecastData.quotations?.length || 0} Quotation Predictions
              </Typography>

              {(forecastData.quotations || []).map((q, qi) => (
                <Card key={qi} elevation={0} sx={{
                  borderRadius: 4, border: '1px solid #f1f5f9', mb: 2, overflow: 'hidden',
                  bgcolor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
                }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap', gap: 1
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                      <Avatar sx={{
                        bgcolor: getProbabilityColor(q.forecast?.predicted_probability || 0),
                        color: 'white', width: 34, height: 34, fontWeight: 800, fontSize: '0.7rem', borderRadius: 2
                      }}>
                        {q.quotation_id ? q.quotation_id.slice(-2) : 'QT'}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight="700" sx={{ color: '#1e293b', fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {q.project_name || q.quotation_id || '—'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {q.customer || '—'} • {q.quotation_id || '—'} • {q.age_days != null ? `${q.age_days}d` : '—'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        icon={getProbabilityIcon(q.forecast?.predicted_probability || 0)}
                        label={`${((q.forecast?.predicted_probability || 0) * 100).toFixed(0)}%`}
                        size="small"
                        sx={{
                          fontWeight: 800, borderRadius: 1.5, height: 26,
                          bgcolor: `${getProbabilityColor(q.forecast?.predicted_probability || 0)}15`,
                          color: getProbabilityColor(q.forecast?.predicted_probability || 0),
                        }}
                      />
                      <Chip
                        label={q.forecast?.target_horizon || '—'}
                        size="small"
                        sx={{
                          fontWeight: 700, borderRadius: 1.5, height: 26,
                          bgcolor: `${getHorizonColor(q.forecast?.target_horizon || '')}15`,
                          color: getHorizonColor(q.forecast?.target_horizon || ''),
                        }}
                      />
                      <Typography fontWeight="700" color="#10b981" fontSize="0.85rem" sx={{ whiteSpace: 'nowrap' }}>
                        {formatCurrency(q.total_amount)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ p: 2 }}>
                    {(q.forecast?.reasoning) && (
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', mb: 1.5, border: '1px solid #f1f5f9' }}>
                        <Typography variant="caption" fontWeight="700" color="#1e3c72" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <InsightsIcon sx={{ fontSize: 13 }} /> AI Reasoning
                        </Typography>
                        <Typography variant="body2" color="#475569" sx={{ fontSize: '0.78rem', lineHeight: 1.5 }}>
                          {q.forecast.reasoning}
                        </Typography>
                      </Box>
                    )}

                    {/* Line Items Table */}
                    {q.line_items && q.line_items.length > 0 && (
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <ChecklistIcon sx={{ fontSize: 13 }} /> Line Items ({q.line_items.length})
                        </Typography>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #f1f5f9', overflow: 'hidden' }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#fafbfc' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.7rem', py: 0.75 }}>Product</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.7rem', py: 0.75, textAlign: 'center' }}>Qty</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.7rem', py: 0.75, textAlign: 'right' }}>Price</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#64748b', fontSize: '0.7rem', py: 0.75, textAlign: 'right' }}>Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {q.line_items.map((item, li) => (
                                <TableRow key={li} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                  <TableCell sx={{ color: '#334155', fontSize: '0.72rem', fontWeight: 600, py: 0.75 }}>
                                    {item.product_name || '—'}
                                  </TableCell>
                                  <TableCell sx={{ color: '#475569', fontSize: '0.72rem', textAlign: 'center', py: 0.75 }}>
                                    {item.qty != null ? item.qty : '—'}
                                  </TableCell>
                                  <TableCell sx={{ color: '#475569', fontSize: '0.72rem', textAlign: 'right', py: 0.75 }}>
                                    {item.price != null ? formatCurrency(item.price) : '—'}
                                  </TableCell>
                                  <TableCell sx={{ color: '#10b981', fontSize: '0.72rem', fontWeight: 700, textAlign: 'right', py: 0.75 }}>
                                    {formatCurrency((item.price || 0) * (item.qty || 0))}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Typography variant="caption" fontWeight="700" color="#64748b" sx={{ minWidth: 95 }}>
                        Expected Value
                      </Typography>
                      <Box sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                        <Box sx={{
                          height: '100%', borderRadius: 4,
                          bgcolor: getProbabilityColor(q.forecast?.predicted_probability || 0),
                          width: `${Math.min(100, (q.forecast?.predicted_probability || 0) * 100)}%`,
                          transition: 'width 0.8s ease'
                        }} />
                      </Box>
                      <Typography variant="body2" fontWeight="700" color={getProbabilityColor(q.forecast?.predicted_probability || 0)}>
                        {formatCurrency(q.forecast?.expected_weighted_value || 0)}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, bgcolor: 'white', borderTop: '1px solid #f1f5f9', justifyContent: 'center' }}>
          <Button onClick={handleCloseForecast} sx={{ borderRadius: 2, fontWeight: 600, color: '#64748b', px: 3 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
export default QuoteDashboard;